import { useEffect } from "react";
import { createPortal } from "react-dom";
import { FiAlertCircle } from "react-icons/fi";
import type { WorkflowStage } from "./WorkflowStageStepper";

interface Props {
    isOpen: boolean;
    targetStage: WorkflowStage;
    unsavedCount: number;
    onCancel: () => void;
    onContinue: () => void;
}

const STAGE_LABELS: Record<2 | 3, string> = {
    2: "Dental Accuracy",
    3: "Video Generation",
};

export default function UnsavedStageConfirmModal({
    isOpen,
    targetStage,
    unsavedCount,
    onCancel,
    onContinue,
}: Props) {
    useEffect(() => {
        if (!isOpen) return;

        const scrollY = window.scrollY;
        const previousOverflow = document.body.style.overflow;
        const previousPosition = document.body.style.position;
        const previousTop = document.body.style.top;
        const previousWidth = document.body.style.width;

        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";

        return () => {
            document.body.style.overflow = previousOverflow;
            document.body.style.position = previousPosition;
            document.body.style.top = previousTop;
            document.body.style.width = previousWidth;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen]);

    if (!isOpen || (targetStage !== 2 && targetStage !== 3)) return null;

    const stageLabel = STAGE_LABELS[targetStage];

    return createPortal(
        <div
            className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="unsaved-stage-title"
        >
            <div className="w-full max-w-md rounded-2xl bg-[#1a1a1a] border border-amber-500/20 p-5 sm:p-6 shadow-[0_0_40px_rgba(245,158,11,0.1)]">
                <div className="flex items-start gap-3 sm:gap-4 mb-4">
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                        <FiAlertCircle className="w-5 h-5 text-amber-400" />
                    </div>
                    <div className="min-w-0">
                        <h2 id="unsaved-stage-title" className="text-lg sm:text-xl font-bold text-amber-400">
                            Unsaved generated images
                        </h2>
                        <p className="text-gray-400 text-sm mt-1">
                            Continue to {stageLabel}?
                        </p>
                    </div>
                </div>

                <p className="text-gray-300 text-sm leading-relaxed mb-6 sm:pl-[3.75rem]">
                    You have{" "}
                    <span className="font-semibold text-white">
                        {unsavedCount} unsaved after {unsavedCount === 1 ? "image" : "images"}
                    </span>
                    .
                </p>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-5 py-2.5 rounded-xl border border-white/10 bg-[#101211] text-gray-300 font-medium hover:text-white hover:border-white/20 transition-colors"
                    >
                        Stay &amp; save
                    </button>
                    <button
                        type="button"
                        onClick={onContinue}
                        className="px-5 py-2.5 rounded-xl bg-[#2fa9bf] text-white font-medium hover:bg-[#36bfd8] transition-colors"
                    >
                        Continue anyway
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
